import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  ValidatorFn,
} from '@angular/forms';
import { DynamicFieldConfig, ValidatorConfig } from './form-config.model';
import { Observable, isObservable, of } from 'rxjs';
import { DropdownOption } from '../models/picklist.model';
import {
  phoneValidator,
  noFutureDateValidator,
  noPastDateValidator,
  textOnlyValidator,
  futureDateOnlyValidator,
  cnicValidator,
  ibanValidator,
  numbersOnlyValidator,
  alphaNumericValidator,
  passportValidator,
  minAgeValidator,
  nonNegativeDecimalValidator,
  knownEmailProviderValidator,
  noEdgeSpacesValidator,
  noUppercaseValidator,
  noIdenticalDigitsValidator,
} from './custom-validators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { blockManualDateEntry as blockManualDateEntryUtil } from './date-input.utils';

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  template: `
    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6"
    >
      @for (field of fields; track field.key) {
        <div [hidden]="field.hidden">
          <!-- Field Wrapper -->
          <div [class]="field.gridClass || 'col-span-1'" class="flex flex-col">
            <label [for]="field.key" class="form-label mb-2">
              {{ field.label | translate }}
              @if (isRequired(field)) {
                <span class="text-red-500">*</span>
              }
            </label>

            @switch (field.type) {
              <!-- RADIO (Toggle Style) -->
              @case ('radio') {
                <div class="radio-toggle-group">
                  @for (opt of (getOptions(field) | async); track opt.value) {
                    <input
                      type="radio"
                      [id]="field.key + '_' + opt.value"
                      [name]="field.key"
                      [formControlName]="field.key"
                      [value]="opt.value"
                    />
                    <label [for]="field.key + '_' + opt.value">
                      {{ opt.label | translate }}
                    </label>
                  }
                </div>
              }

              <!-- SEARCHABLE SELECT -->
              @case ('select') {
                @if (field.searchable) {
                  @let opts = (getOptions(field) | async) ?? [];
                  <div class="relative" (click)="$event.stopPropagation()">
                    <button
                      type="button"
                      [id]="field.key"
                      class="form-select text-start"
                      [class.border-red-500]="isInvalid(field.key)"
                      [disabled]="form.get(field.key)?.disabled"
                      (click)="toggleSearchSelect(field.key)"
                    >
                      {{ (getSelectedLabel(opts, field.key) | translate) || ('COMMON.SELECT_FIELD' | translate: { field: (field.label | translate) }) }}
                    </button>

                    @if (openSearchKey === field.key) {
                      <div class="mt-1 border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div class="p-2 border-b border-gray-100">
                          <input
                            type="text"
                            class="form-input"
                            [placeholder]="'COMMON.SEARCH_PLACEHOLDER' | translate"
                            autocomplete="off"
                            [value]="searchTerms[field.key] || ''"
                            (input)="searchTerms[field.key] = $any($event.target).value"
                          />
                        </div>

                        <ul class="max-h-60 overflow-y-auto py-1">
                          @for (opt of filterOptions(opts, field.key); track opt.value) {
                            <li
                              class="px-3 py-2 text-sm cursor-pointer hover:bg-indigo-50"
                              [class.bg-indigo-100]="form.get(field.key)?.value === opt.value"
                              (click)="selectSearchOption(field.key, opt.value)"
                            >
                              {{ opt.label | translate }}
                            </li>
                          } @empty {
                            <li class="px-3 py-2 text-sm text-gray-400">{{ 'COMMON.NO_RESULTS_FOUND' | translate }}</li>
                          }
                        </ul>
                      </div>
                    }
                  </div>
                } @else {
                <div class="relative">
                  <select
                    [formControlName]="field.key"
                    [id]="field.key"
                    class="form-select"
                    [class.pe-9]="field.clearable && form.get(field.key)?.value"
                    [class.bg-gray-100]="field.clearable && form.get(field.key)?.value"
                    [class.border-red-500]="isInvalid(field.key)"
                    [attr.tabindex]="field.clearable && form.get(field.key)?.value ? -1 : null"
                  >
                    <option [ngValue]="null">{{ 'COMMON.SELECT_FIELD' | translate: { field: (field.label | translate) } }}</option>
                    @for (opt of (getOptions(field) | async); track opt.value) {
                      <option [ngValue]="opt.value">
                        {{ opt.label | translate }}
                      </option>
                    }
                  </select>

                  @if (field.clearable && form.get(field.key)?.value) {
                    <!-- Blocks interaction with the select until cleared, without disabling the FormControl (keeps its value in the submitted form) -->
                    <div class="absolute inset-0 cursor-not-allowed" (mousedown)="$event.preventDefault()"></div>

                    <button
                      type="button"
                      class="absolute end-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-500 hover:bg-red-200"
                      (click)="clearField(field.key)"
                      [attr.aria-label]="'COMMON.CLEAR' | translate"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  }
                </div>
                }
              }

              <!-- TEXTAREA -->
              @case ('textarea') {
                <textarea
                  [formControlName]="field.key"
                  [id]="field.key"
                  class="form-textarea"
                  [placeholder]="field.placeholder ? (field.placeholder | translate) : ''"
                  [class.border-red-500]="isInvalid(field.key)"
                  [rows]="field.rows || 4"
                ></textarea>
              }

              <!-- CHECKBOX -->
              @case ('checkbox') {
                <div class="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    [formControlName]="field.key"
                    [id]="field.key"
                    class="w-5 h-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  />
                  <label
                    [for]="field.key"
                    class="text-sm text-gray-700 cursor-pointer select-none"
                    >{{ (field.placeholder || 'COMMON.YES') | translate }}</label
                  >
                </div>
              }

              <!-- FILE (Image Upload with Preview) -->
              @case ('file') {
                <div class="flex flex-col gap-2">
                  <input
                    type="file"
                    [id]="field.key"
                    [accept]="field.accept || 'image/jpeg,image/jpg,image/png'"
                    [disabled]="field.disabled"
                    (change)="onFileSelected($event, field)"
                    class="form-input"
                  />
                  @if (form.get(field.key)?.value; as preview) {
                    @if (isImageDataUrl(preview)) {
                      <img
                        [src]="preview"
                        [alt]="'COMMON.PREVIEW' | translate"
                        class="mt-2 max-h-40 rounded border object-contain"
                      />
                    } @else {
                      <div class="flex items-center justify-between mt-1">
                        <p class="text-sm text-gray-600">{{ 'COMMON.DOCUMENT_UPLOADED' | translate }}</p>
                        @if (showFileDownload) {
                          <a
                            [href]="preview"
                            [download]="getDownloadFileName(field, preview)"
                            target="_blank"
                            class="text-sm text-indigo-600 hover:underline"
                          >
                            {{ 'COMMON.DOWNLOAD' | translate }}
                          </a>
                        }
                      </div>
                    }
                  }
                </div>
              }

              <!-- DEFAULT INPUT (Text, Number, Date, etc) -->
              @default {
                <input
                  [type]="field.type"
                  [formControlName]="field.key"
                  [id]="field.key"
                  class="form-input"
                  [placeholder]="field.placeholder ? (field.placeholder | translate) : ''"
                  [class.border-red-500]="isInvalid(field.key)"
                  [readOnly]="field.readonly && field.type !== 'date'"
                  (keydown)="field.type === 'date' ? blockManualDateEntry($event) : null"
                  (paste)="field.type === 'date' ? $event.preventDefault() : null"
                  [attr.maxlength]="field.maxlength || null"
                  [attr.inputmode]="field.inputmode || null"
                  [attr.min]="getDateMin(field)"
                  [attr.max]="getDateMax(field)"
                />
              }
            }

            <!-- ERROR MESSAGES -->
            @if (isInvalid(field.key)) {
              <div class="text-red-500 text-xs mt-1 min-h-[1rem] animate-fade-in">
                {{ getErrorMessage(field) }}
              </div>
            }
          </div>
        </div>
      }
    </form>
  `,
})
export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() fields: DynamicFieldConfig[] = [];
  @Input() model: any = {}; // Initial data
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formChange = new EventEmitter<any>(); // Live updates if needed
  @Input() readonly = false;
  /** Hides the "Download" link on file fields — used to keep it out of the Add popup, showing it only for Edit/View. */
  @Input() showFileDownload = true;
  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
  }
  clearField(key: string): void {
    this.form.get(key)?.setValue(null);
  }

  // ================= SEARCHABLE SELECT =================
  openSearchKey: string | null = null;
  searchTerms: Record<string, string> = {};

  @HostListener('document:click')
  closeSearchSelect(): void {
    if (this.openSearchKey) {
      this.form.get(this.openSearchKey)?.markAsTouched();
      this.openSearchKey = null;
    }
  }

  toggleSearchSelect(key: string): void {
    if (this.openSearchKey === key) {
      this.closeSearchSelect();
      return;
    }

    this.openSearchKey = key;
    this.searchTerms[key] = '';
  }

  selectSearchOption(key: string, value: any): void {
    const control = this.form.get(key);
    control?.setValue(value);
    control?.markAsDirty();
    this.closeSearchSelect();
  }

  filterOptions(options: DropdownOption[], key: string): DropdownOption[] {
    const term = (this.searchTerms[key] || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => String(opt.label ?? '').toLowerCase().includes(term));
  }

  getSelectedLabel(options: DropdownOption[], key: string): string {
    const value = this.form.get(key)?.value;
    if (value === null || value === undefined) return '';
    return options.find((opt) => opt.value === value)?.label ?? '';
  }

  patchValue(data: any): void {
    if (this.form) {
      this.form.patchValue(data);
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields']) {
      this.buildForm();
    }

    if (changes['model'] && this.form) {
      this.patchChangedModelValues();
    }

    if (changes['readonly'] && this.form) {
      if (this.readonly) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }
  /**
   * Hosts often echo `formChange` straight back as `model`. Re-writing a value
   * the control already holds still rewrites the input, which on a number
   * field mid-typing ("25." reads as 25) throws the caret to the start — so
   * only keys whose value actually differs are patched.
   */
  private patchChangedModelValues(): void {
    const model = this.model || {};
    const changed: Record<string, any> = {};

    Object.keys(model).forEach((key) => {
      const control = this.form.get(key);

      if (control && control.value !== model[key]) {
        changed[key] = model[key];
      }
    });

    if (Object.keys(changed).length) {
      this.form.patchValue(changed);
    }
  }

  ngAfterViewInit(): void {
    const kiborFlagCtrl = this.form.get('acrdCalcFlg');
    const startCtrl = this.form.get('vkbr_strt_dt');
    const endCtrl = this.form.get('vkbr_end_dt');
    const vkbrCtrl = this.form.get('vkbr_rt');
    const kiborResetPeriod = this.form.get('kiborResetPeriod');

    if (kiborFlagCtrl && startCtrl && endCtrl) {
      kiborFlagCtrl.valueChanges.subscribe((value) => {
        if (Number(value) === 1) {
          startCtrl.enable();
          endCtrl.enable();
          vkbrCtrl?.enable();
          kiborResetPeriod?.disable(); // ✅ FIXED
        } else {
          startCtrl.disable();
          endCtrl.disable();
          vkbrCtrl?.disable();
          kiborResetPeriod?.enable(); // ✅ OK

          startCtrl.setValue(null);
          endCtrl.setValue(null);
          vkbrCtrl?.setValue(null);
        }
      });

      // initial state
      if (Number(kiborFlagCtrl.value) !== 1) {
        startCtrl.disable();
        endCtrl.disable();
      }
    }
    const coveringEmpCtrl = this.form.get('coveringEmployee');
    const coveringFromDateCtrl = this.form.get('fromDate');
    const coveringToDateCtrl = this.form.get('toDate');

    if (coveringEmpCtrl && coveringFromDateCtrl && coveringToDateCtrl) {
      const toggleCoveringDates = (value: any) => {
        if (value) {
          coveringFromDateCtrl.setValidators([Validators.required]);
          coveringToDateCtrl.setValidators([Validators.required]);
        } else {
          coveringFromDateCtrl.clearValidators();
          coveringToDateCtrl.clearValidators();
          coveringFromDateCtrl.setValue(null);
          coveringToDateCtrl.setValue(null);
        }
        coveringFromDateCtrl.updateValueAndValidity({ emitEvent: false });
        coveringToDateCtrl.updateValueAndValidity({ emitEvent: false });
      };

      coveringEmpCtrl.valueChanges.subscribe(toggleCoveringDates);
      toggleCoveringDates(coveringEmpCtrl.value);
    }

    const scopeCtrl = this.form.get('scopeType');
    const productCtrl = this.form.get('productId');

    if (!scopeCtrl || !productCtrl) return;

    // 🔁 React on toggle change
    scopeCtrl.valueChanges.subscribe((value) => {
      if (value === 'GLOBAL') {
        productCtrl.disable(); // ❌ cannot select
        productCtrl.setValue(null);
      } else {
        productCtrl.enable(); // ✅ selectable
      }
    });

    // 🔒 Initial state
    if (scopeCtrl.value === 'GLOBAL') {
      productCtrl.disable();
    }
  }

  buildForm(): void {
    const group: any = {};

    this.fields.forEach((field) => {
      const validators = this.mapValidators(field.validations);

      const value =
        this.model[field.key] !== undefined ? this.model[field.key] : (field.value ?? null);

      const control = new FormControl({ value, disabled: field.disabled || false }, validators);

      group[field.key] = control;
    });

    this.form = this.fb.group(group);
    if (this.readonly) {
      this.form.disable();
    }
    this.form.valueChanges.subscribe((val) => {
      this.formChange.emit(val);
    });
  }
  readonly blockManualDateEntry = blockManualDateEntryUtil;

  getDateMin(field: DynamicFieldConfig): string | null {
    if (field.type !== 'date' || !field.validations) return null;

    if (field.validations.futureDateOnly) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return this.toISODate(tomorrow);
    }

    if (field.validations.noPastDate) {
      return this.toISODate(new Date());
    }

    return null;
  }

  getDateMax(field: DynamicFieldConfig): string | null {
    if (field.type !== 'date' || !field.validations) return null;

    if (field.validations.minAge) {
      const today = new Date();
      const maxDob = new Date(
        today.getFullYear() - field.validations.minAge,
        today.getMonth(),
        today.getDate(),
      );
      return this.toISODate(maxDob);
    }

    if (field.validations.noFuturDate) {
      return this.toISODate(new Date());
    }

    return null;
  }

  private toISODate(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  mapValidators(config?: ValidatorConfig): ValidatorFn[] {
    if (!config) return [];
    const validators: ValidatorFn[] = [];

    if (config.required) validators.push(Validators.required);
    if (config.minLength) validators.push(Validators.minLength(config.minLength));
    if (config.maxLength) validators.push(Validators.maxLength(config.maxLength));
    if (config.pattern) validators.push(Validators.pattern(config.pattern));
    if (config.min !== undefined && config.min !== null) {
      validators.push(Validators.min(config.min));
    }

    if (config.max !== undefined && config.max !== null) {
      validators.push(Validators.max(config.max));
    }
    if (config.email) validators.push(Validators.email);
    if (config.knownEmailProviderDomain) validators.push(knownEmailProviderValidator());
    if (config.textOnly) validators.push(textOnlyValidator());
    if (config.phone) validators.push(phoneValidator());
    if (config.noFuturDate) validators.push(noFutureDateValidator());
    if (config.futureDateOnly) validators.push(futureDateOnlyValidator());
    if (config.noPastDate) validators.push(noPastDateValidator());
    if (config.cnic) validators.push(cnicValidator());
    if (config.iban) validators.push(ibanValidator());
    if (config.numbersOnly) validators.push(numbersOnlyValidator());
    if (config.alphaNumeric) validators.push(alphaNumericValidator());
    if (config.noEdgeSpaces) validators.push(noEdgeSpacesValidator());
    if (config.noUppercase) validators.push(noUppercaseValidator());
    if (config.noIdenticalDigits) validators.push(noIdenticalDigitsValidator());
    if (config.passport) validators.push(passportValidator());
    if (config.minAge) validators.push(minAgeValidator(config.minAge));
    if (config.nonNegativeDecimal) {
      validators.push(nonNegativeDecimalValidator(config.decimalPlaces ?? 2));
    }
    return validators;
  }

  onFileSelected(event: Event, field: DynamicFieldConfig): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const control = this.form.get(field.key);

    if (!file || !control) {
      return;
    }

    const allowedTypes = field.allowedFileTypes?.length
      ? field.allowedFileTypes
      : ['image/jpeg', 'image/jpg', 'image/png'];

    if (!allowedTypes.includes(file.type)) {
      control.setValue(null);
      control.setErrors({ invalidFileType: true });
      control.markAsTouched();
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      control.setValue(reader.result);
      control.markAsTouched();
      control.markAsDirty();
    };
    reader.readAsDataURL(file);
  }

  isImageDataUrl(value: unknown): boolean {
    return typeof value === 'string' && value.startsWith('data:image');
  }

  private static readonly EXT_BY_MIME: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  };

  getDownloadFileName(field: DynamicFieldConfig, dataUrl: string): string {
    const match = /^data:(.+?);base64,/.exec(dataUrl || '');
    const mimeType = match?.[1] || '';
    const extension = DynamicFormComponent.EXT_BY_MIME[mimeType] || 'bin';

    return `${field.label ? this.translate.instant(field.label) : 'document'}.${extension}`;
  }

  // Helper for Template
  getOptions(field: DynamicFieldConfig): Observable<DropdownOption[]> {
    if (isObservable(field.options$)) return field.options$;
    return of(field.options$ || []);
  }

  isRequired(field: DynamicFieldConfig): boolean {
    if (field.validations?.required) return true;
    return !!this.form.get(field.key)?.hasValidator(Validators.required);
  }

  isInvalid(key: string): boolean {
    const control = this.form.get(key);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getErrorMessage(field: DynamicFieldConfig): string {
    const control = this.form.get(field.key);
    if (!control || !control.errors) return '';

    const errors = control.errors;
    const t = (key: string, params?: Record<string, unknown>) =>
      this.translate.instant('VALIDATION.' + key, params);

    // 1. Check Custom Messages
    if (field.errorMessages) {
      for (const errKey in errors) {
        if (field.errorMessages[errKey]) return this.translate.instant(field.errorMessages[errKey]);
      }
    }
    if (errors['nonNegativeDecimal']) {
      return t('NON_NEGATIVE_DECIMAL', { places: errors['nonNegativeDecimal'].decimalPlaces });
    }
    // 2. Default Messages
    if (errors['required'])
      return t('REQUIRED', { field: field.label ? this.translate.instant(field.label) : '' });
    if (errors['minlength']) return t('MIN_LENGTH', { min: errors['minlength'].requiredLength });
    if (errors['maxlength']) return t('MAX_LENGTH', { max: errors['maxlength'].requiredLength });
    if (errors['pattern']) return t('INVALID_FORMAT');
    if (errors['email']) return t('INVALID_EMAIL');
    if (errors['knownEmailProviderDomain']) {
      return t('KNOWN_EMAIL_PROVIDER_DOMAIN', { host: errors['knownEmailProviderDomain'].host });
    }
    if (errors['min']) return t('MIN_VALUE', { min: errors['min'].min });
    if (errors['max']) return t('MAX_VALUE', { max: errors['max'].max });

    if (errors['phoneSpecialChar']) return t('PHONE_DIGITS_ONLY');
    if (errors['phonePrefix']) return t('PHONE_PREFIX');
    if (errors['phoneMinDigits']) return t('PHONE_MIN_DIGITS', { min: errors['phoneMinDigits'].min });
    if (errors['phoneMaxDigits']) return t('PHONE_MAX_DIGITS', { max: errors['phoneMaxDigits'].max });
    if (errors['noFutureDate']) return t('NO_FUTURE_DATE');
    if (errors['textOnly']) return t('TEXT_ONLY');
    if (errors['futureDateOnly']) return t('FUTURE_DATE_ONLY'); // ✅ NEW
    if (errors['noPastDate']) return t('NO_PAST_DATE');

    // ✅ NEW — CNIC
    if (errors['cnicSpecialChar']) return t('CNIC_DIGITS_ONLY');
    if (errors['cnicStartsWithZero']) {
      return t('CNIC_STARTS_WITH_ZERO');
    }
    if (errors['cnicMinDigits']) return t('CNIC_LENGTH', { length: errors['cnicMinDigits'].min });
    if (errors['cnicMaxDigits']) return t('CNIC_LENGTH', { length: errors['cnicMaxDigits'].max });
    if (errors['cnicAllIdenticalDigits']) return t('CNIC_IDENTICAL_DIGITS');

    if (errors['ibanSpecialChar']) return t('IBAN_ALPHANUMERIC');
    if (errors['ibanMinLength']) return t('IBAN_LENGTH', { length: errors['ibanMinLength'].min });
    if (errors['ibanMaxLength']) return t('IBAN_LENGTH', { length: errors['ibanMaxLength'].max });

    if (errors['numbersOnly']) return t('NUMBERS_ONLY');

    if (errors['alphaNumeric']) return t('ALPHA_NUMERIC');
    if (errors['noEdgeSpaces']) return t('NO_EDGE_SPACES');
    if (errors['noUppercase']) return t('NO_UPPERCASE');
    if (errors['identicalDigits']) return t('IDENTICAL_DIGITS');
    // ✅ Passport
    if (errors['passportInvalidFormat']) {
      return t('PASSPORT_ALPHANUMERIC');
    }

    if (errors['passportMinLength']) {
      return t('PASSPORT_MIN_LENGTH', { min: errors['passportMinLength'].min });
    }

    if (errors['passportMaxLength']) {
      return t('PASSPORT_MAX_LENGTH', { max: errors['passportMaxLength'].max });
    }

    if (errors['passportLetterRequired']) {
      return t('PASSPORT_LETTER_REQUIRED');
    }

    if (errors['passportNumberRequired']) {
      return t('PASSPORT_NUMBER_REQUIRED');
    }
    // ✅ Date of Birth
    if (errors['dobFuture']) return t('DOB_FUTURE');
    if (errors['invalidDate']) return t('INVALID_DATE');
    if (errors['minAge']) {
      return t('MIN_AGE', { age: errors['minAge'].requiredAge });
    }
    if (errors['invalidFileType']) {
      return t('INVALID_IMAGE_TYPE');
    }
    return t('INVALID_FIELD');
  }

  submit(): void {
    if (this.form.valid) {
      this.formSubmit.emit(this.form.value);
    } else {
      this.form.markAllAsTouched();
    }
  }

  onSubmit(): void {
    this.submit();
  }
}
